import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  MessageSquare, 
  Search, 
  Filter, 
  Eye, 
  Edit2, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Mail,
  User,
  Calendar,
  Tag
} from 'lucide-react';
import { theme } from '../styles/theme';
import { Button, Input, Select, Card, LoadingSpinner } from '../styles/GlobalStyles';

// Types
interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  inquiry_type: string;
  priority: string;
  status: string;
  user_id?: string;
  admin_notes?: string;
  response_sent: boolean;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  assigned_to?: string;
}

interface ContactStats {
  total_submissions: number;
  pending_submissions: number;
  in_progress_submissions: number;
  resolved_submissions: number;
  submissions_by_type: { [key: string]: number };
  submissions_by_priority: { [key: string]: number };
  recent_submissions: ContactSubmission[];
  avg_response_time_hours?: number;
}

// Styled Components
const AdminContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[8]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${theme.spacing[4]};
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  h1 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.textPrimary};
    display: flex;
    align-items: center;
    gap: ${theme.spacing[3]};
    margin-bottom: ${theme.spacing[2]};
  }
  
  p {
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
`;

const StatCard = styled(Card)`
  padding: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.$color}20;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatContent = styled.div`
  flex: 1;
  
  h3 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.textPrimary};
    margin-bottom: ${theme.spacing[1]};
  }
  
  p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};
    margin: 0;
  }
`;

const FiltersBar = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  
  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
  
  @media (max-width: ${theme.breakpoints.md}) {
    max-width: none;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
`;

const SearchInputField = styled(Input)`
  padding-left: ${theme.spacing[10]};
`;

const SubmissionsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.base};
`;

const TableHeader = styled.th`
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[4]};
  text-align: left;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  border-bottom: 1px solid ${theme.colors.border};
  font-size: ${theme.typography.fontSize.sm};
`;

const TableCell = styled.td`
  padding: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  vertical-align: top;
`;

const StatusBadge = styled.div<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  
  ${({ $status }) => {
    switch ($status) {
      case 'pending':
        return `
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[700]};
        `;
      case 'in_progress':
        return `
          background: ${theme.colors.info[100]};
          color: ${theme.colors.info[700]};
        `;
      case 'resolved':
        return `
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
        `;
      case 'closed':
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[800]};
        `;
      default:
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[800]};
        `;
    }
  }}
`;

const PriorityBadge = styled.div<{ $priority: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  
  ${({ $priority }) => {
    switch ($priority) {
      case 'urgent':
        return `
          background: ${theme.colors.error[100]};
          color: ${theme.colors.error[700]};
        `;
      case 'high':
        return `
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[700]};
        `;
      case 'medium':
        return `
          background: ${theme.colors.info[100]};
          color: ${theme.colors.info[700]};
        `;
      case 'low':
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[800]};
        `;
      default:
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[800]};
        `;
    }
  }}
`;

const InquiryTypeBadge = styled.div`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: ${theme.colors.accent[100]};
  color: ${theme.colors.accent[800]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const ActionButton = styled.button<{ $variant?: 'view' | 'edit' | 'delete' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.surface};
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  margin-right: ${theme.spacing[2]};
  
  &:hover {
    ${({ $variant }) => {
      switch ($variant) {
        case 'view':
          return `
            border-color: ${theme.colors.info[500]};
            color: ${theme.colors.info[600]};
            background: ${theme.colors.info[100]};
          `;
        case 'edit':
          return `
            border-color: ${theme.colors.warning[500]};
            color: ${theme.colors.warning[600]};
            background: ${theme.colors.warning[100]};
          `;
        case 'delete':
          return `
            border-color: ${theme.colors.error[500]};
            color: ${theme.colors.error[600]};
            background: ${theme.colors.error[100]};
          `;
        default:
          return `
            border-color: ${theme.colors.primary[500]};
            color: ${theme.colors.primary[600]};
            background: ${theme.colors.primary[50]};
          `;
      }
    }}
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing[6]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
`;

const PaginationInfo = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
`;

const PaginationControls = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const PaginationButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.surface};
  color: ${({ $disabled }) => $disabled ? theme.colors.textDisabled : theme.colors.textSecondary};
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: all ${theme.transitions.base};
  
  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
    background: ${theme.colors.primary[50]};
  }
`;

const TruncatedText = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  
  &:hover {
    background: ${theme.colors.primary[50]};
    padding: ${theme.spacing[1]};
    border-radius: ${theme.borderRadius.sm};
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]};
  color: ${theme.colors.textSecondary};
  
  h3 {
    font-size: ${theme.typography.fontSize.lg};
    margin-bottom: ${theme.spacing[2]};
  }
  
  p {
    font-size: ${theme.typography.fontSize.sm};
  }
`;

// Helper functions
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatInquiryType = (type: string): string => {
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending':
      return <Clock size={14} />;
    case 'in_progress':
      return <AlertTriangle size={14} />;
    case 'resolved':
      return <CheckCircle size={14} />;
    case 'closed':
      return <XCircle size={14} />;
    default:
      return <Clock size={14} />;
  }
};

const ContactAdmin: React.FC = () => {
  const [stats, setStats] = useState<ContactStats | null>(null);
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  // Fetch stats and submissions
  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      // Fetch stats
      const statsResponse = await fetch('/admin/contact/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('inquiry_type', typeFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      
      // Fetch submissions
      const submissionsResponse = await fetch(`/admin/contact/submissions?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (submissionsResponse.ok) {
        const submissionsData = await submissionsResponse.json();
        setSubmissions(submissionsData.submissions);
        setTotalPages(Math.ceil(submissionsData.total / 20));
      }
      
    } catch (error) {
      console.error('Error fetching contact data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, searchTerm, statusFilter, typeFilter, priorityFilter]);

  const handleViewSubmission = (submission: ContactSubmission) => {
    // TODO: Implement modal to view full submission details
    console.log('View submission:', submission);
  };

  const handleEditSubmission = (submission: ContactSubmission) => {
    // TODO: Implement edit submission modal
    console.log('Edit submission:', submission);
  };

  if (loading && !stats) {
    return (
      <AdminContainer>
        <div style={{ display: 'flex', justifyContent: 'center', padding: theme.spacing[12] }}>
          <LoadingSpinner size="lg" />
        </div>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <Header>
        <HeaderLeft>
          <h1>
            <MessageSquare size={28} />
            Contact Submissions
          </h1>
          <p>Manage and respond to user inquiries, feedback, and support requests</p>
        </HeaderLeft>
      </Header>

      {/* Statistics */}
      {stats && (
        <StatsGrid>
          <StatCard>
            <StatIcon $color={theme.colors.primary[500]}>
              <MessageSquare size={24} />
            </StatIcon>
            <StatContent>
              <h3>{stats.total_submissions}</h3>
              <p>Total Submissions</p>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon $color={theme.colors.warning[500]}>
              <Clock size={24} />
            </StatIcon>
            <StatContent>
              <h3>{stats.pending_submissions}</h3>
              <p>Pending Review</p>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon $color={theme.colors.info[500]}>
              <AlertTriangle size={24} />
            </StatIcon>
            <StatContent>
              <h3>{stats.in_progress_submissions}</h3>
              <p>In Progress</p>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon $color={theme.colors.success[500]}>
              <CheckCircle size={24} />
            </StatIcon>
            <StatContent>
              <h3>{stats.resolved_submissions}</h3>
              <p>Resolved</p>
            </StatContent>
          </StatCard>
        </StatsGrid>
      )}

      {/* Filters */}
      <FiltersBar>
        <SearchInput>
          <SearchIcon>
            <Search size={16} />
          </SearchIcon>
          <SearchInputField
            type="text"
            placeholder="Search submissions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
        
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </Select>
        
        <Select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All Types</option>
          <option value="technical_support">Technical Support</option>
          <option value="feature_request">Feature Request</option>
          <option value="bug_report">Bug Report</option>
          <option value="billing_question">Billing Question</option>
          <option value="partnership_inquiry">Partnership</option>
          <option value="general_question">General Question</option>
          <option value="feedback">Feedback</option>
          <option value="other">Other</option>
        </Select>
        
        <Select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
        >
          <option value="">All Priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </Select>
      </FiltersBar>

      {/* Submissions Table */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: theme.spacing[12] }}>
          <LoadingSpinner size="lg" />
        </div>
      ) : submissions.length === 0 ? (
        <NoDataMessage>
          <MessageSquare size={48} style={{ marginBottom: theme.spacing[4], opacity: 0.5 }} />
          <h3>No submissions found</h3>
          <p>There are no contact submissions matching your current filters.</p>
        </NoDataMessage>
      ) : (
        <>
          <SubmissionsTable>
            <thead>
              <tr>
                <TableHeader>Submission</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Priority</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Created</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {submissions.map((submission) => (
                <tr key={submission.id}>
                  <TableCell>
                    <div>
                      <div style={{ fontWeight: theme.typography.fontWeight.medium, marginBottom: theme.spacing[1] }}>
                        {submission.name}
                      </div>
                      <div style={{ color: theme.colors.textSecondary, fontSize: theme.typography.fontSize.xs, marginBottom: theme.spacing[1] }}>
                        {submission.email}
                      </div>
                      <TruncatedText title={submission.subject}>
                        <strong>{submission.subject}</strong>
                      </TruncatedText>
                      <TruncatedText title={submission.message}>
                        {submission.message}
                      </TruncatedText>
                    </div>
                  </TableCell>
                  <TableCell>
                    <InquiryTypeBadge>
                      {formatInquiryType(submission.inquiry_type)}
                    </InquiryTypeBadge>
                  </TableCell>
                  <TableCell>
                    <PriorityBadge $priority={submission.priority}>
                      {submission.priority.toUpperCase()}
                    </PriorityBadge>
                  </TableCell>
                  <TableCell>
                    <StatusBadge $status={submission.status}>
                      {getStatusIcon(submission.status)}
                      {submission.status.replace('_', ' ').toUpperCase()}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <div style={{ fontSize: theme.typography.fontSize.xs }}>
                      {formatDate(submission.created_at)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <ActionButton
                      $variant="view"
                      onClick={() => handleViewSubmission(submission)}
                      title="View Details"
                    >
                      <Eye size={16} />
                    </ActionButton>
                    <ActionButton
                      $variant="edit"
                      onClick={() => handleEditSubmission(submission)}
                      title="Edit Status"
                    >
                      <Edit2 size={16} />
                    </ActionButton>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </SubmissionsTable>

          {/* Pagination */}
          <Pagination>
            <PaginationInfo>
              Page {page} of {totalPages}
            </PaginationInfo>
            <PaginationControls>
              <PaginationButton
                $disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                <ChevronLeft size={16} />
              </PaginationButton>
              <PaginationButton
                $disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                <ChevronRight size={16} />
              </PaginationButton>
            </PaginationControls>
          </Pagination>
        </>
      )}
    </AdminContainer>
  );
};

export default ContactAdmin;